/**
 * Entity extraction from tool results (A-2) — pure data logic shared by web
 * cards and CLI transcripts. Moved verbatim from
 * `frontend/src/components/agent/AgentPanelBlocks.tsx`; the React
 * `ToolResultEntityCards` stays in the web layer and renders what this
 * returns.
 */

// Tool-name prefix → canonical schema_id. Used when a batch result row has
// `id` but no `schema_id` (e.g. contacts.batch_create rows are
// `{id, name, email, status}` with schema implicit).
const TOOL_PREFIX_TO_SCHEMA: Readonly<Record<string, string>> = {
  contacts: "contacts.person",
  email: "emails.message",
  emails: "emails.message",
  notes: "notes.note",
  projects: "projects.project",
  tasks: "tasks.task",
  telegram: "telegram.message",
  meetings: "meetings.calendar_event",
};

export function inferSchemaFromTool(toolName: string | undefined): string | null {
  if (!toolName) return null;
  const prefix = toolName.split(/[._]/)[0];
  return prefix ? (TOOL_PREFIX_TO_SCHEMA[prefix] ?? null) : null;
}

function withSchema(
  item: Record<string, unknown>,
  fallback: string | null,
): Record<string, unknown> | null {
  if (item.id == null) return null;
  if (typeof item.schema_id === "string") return item;
  if (fallback !== null) return { ...item, schema_id: fallback };
  return null;
}

export interface ExtractEntitiesOptions {
  readonly toolName?: string;
  readonly defaultSchemaId?: string;
}

export function extractEntities(
  result: unknown,
  opts?: ExtractEntitiesOptions,
): readonly Readonly<Record<string, unknown>>[] {
  if (!result || typeof result !== "object") return [];
  const r = result as Record<string, unknown>;
  const fallback = opts?.defaultSchemaId ?? inferSchemaFromTool(opts?.toolName);

  // Single entity: backend tools normally set `schema_id`, but when they
  // don't (raw create helpers, integration glue) we still recognise the
  // row by `id` and inject the tool-name-derived schema as a fallback.
  if (r.id != null) {
    const single = withSchema(r, fallback);
    if (single) return [single];
  }

  // Batch: accept either `items` (direct RPC) or `results` (e.g.
  // contacts.batch_create). Rows may lack schema_id — inject via fallback.
  for (const key of ["items", "results"] as const) {
    const arr = r[key];
    if (Array.isArray(arr)) {
      const rows = (arr as unknown[])
        .filter((i): i is Record<string, unknown> => i != null && typeof i === "object")
        .map((row) => withSchema(row, fallback))
        .filter((row): row is Record<string, unknown> => row !== null);
      if (rows.length > 0) return rows;
    }
  }

  // Unwrap the approval-execute envelope:
  // `{status, tool_name, result: <entity|batch|mcp>}`. Propagate tool_name into
  // opts so deeper unwraps can still infer the schema for schemaless batch rows.
  if (r.result !== undefined && typeof r.result === "object" && r.result !== null) {
    const innerOpts: ExtractEntitiesOptions = {
      defaultSchemaId: opts?.defaultSchemaId,
      toolName: opts?.toolName ?? (typeof r.tool_name === "string" ? r.tool_name : undefined),
    };
    return extractEntities(r.result, innerOpts);
  }

  // MCP ToolResult wrapper: `{content: [{type, text: "<json>"}]}`. execute_tool
  // at the retired Rust crate's src/api/mcp/handler.rs wraps every tool return in this shape
  // before the approval flow hands it back.
  if (Array.isArray(r.content)) {
    const first = r.content[0] as Record<string, unknown> | undefined;
    const text = first?.text;
    if (typeof text === "string") {
      try {
        return extractEntities(JSON.parse(text), opts);
      } catch {
        return [];
      }
    }
  }
  return [];
}
